import {
    useImperativeHandle,
    forwardRef,
    useEffect,
    useRef
} from "react";
import {
    Animated,
    Easing,
    View
} from "react-native";
import type IDialogProps from "./type";
import type {
    IDialogRef
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    RefForwardingComponent
} from "../../types";
import type {
    IModalRef
} from "../modal/type";
import {
    X as XIcon
} from "lucide-react-native";
import {
    uuid
} from "../../utils";
import Button from "../button";
import Modal from "../modal";
import Text from "../text";

const Dialog: RefForwardingComponent<IDialogRef, IDialogProps> = ({
    bottomContentContainerStyle,
    contentJustify = "centered",
    closeAnimationDelay = 100,
    openAnimationDelay = 100,
    contentContainerStyle,
    bottomContainerStyle,
    headerContainerStyle,
    secondaryButtonProps,
    secondaryButtonStyle,
    primaryButtonProps,
    primaryButtonStyle,
    closeIconProps = {
        color: "low",
        size: 22
    },
    isVisible = false,
    withModal = true,
    bottomComponent,
    headerComponent,
    onOverlayPress,
    variant = "ok",
    modalProps,
    id: outID,
    children,
    onClosed,
    content,
    style,
    title
}, ref) => {
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const id = useRef(outID ? outID : uuid());

    const {
        bottomContentContainer: bottomContentContainerDynamicStyle,
        bottomContainer: bottomContainerDynamicStyle,
        headerContainer: headerContainerDynamicStyle,
        primaryButton: primaryButtonDynamicStyle,
        contentText: contentTextDynamicStyle,
        headerTitle: headerTitleDynamicStyle,
        closeIcon: closeIconDynamicStyle,
        container: containerDynamicStyle,
        content: contentDynamicStyle
    } = useStyles({
        contentJustify,
        isVisible,
        radiuses,
        variant,
        colors,
        spaces
    });

    const scaleAnim = useRef(new Animated.Value(0)).current;

    const modalRef = useRef<IModalRef>(null);

    useEffect(() => {
        if(isVisible) {
            Animated.timing(scaleAnim, {
                duration: openAnimationDelay,
                easing: Easing.linear,
                useNativeDriver: true,
                toValue: 1
            }).start();
        }
    }, [
        isVisible
    ]);

    useImperativeHandle(
        ref,
        () => ({
            closeAnimation
        }),
        []
    );

    const closeAnimation = (_onClosed?: (props: {
        id: string;
    }) => void) => {
        Animated.timing(scaleAnim, {
            duration: closeAnimationDelay,
            easing: Easing.linear,
            useNativeDriver: true,
            toValue: 0
        }).start(({
            finished
        }) => {
            if(finished) {
                if(_onClosed) _onClosed({
                    id: id.current
                });

                if(onClosed) onClosed({
                    id: id.current
                });

                if(modalRef && modalRef.current) modalRef.current.closeAnimation();
            }
        });
    };

    const renderHeader = () => {
        return <View
            style={[
                headerContainerStyle,
                stylesheet.headerContainer,
                headerContainerDynamicStyle
            ]}
        >
            {headerComponent || <Text
                style={[
                    stylesheet.headerTitle,
                    headerTitleDynamicStyle
                ]}
                variant="titleLargeSize"
                color="mid"
            >
                {title}
            </Text>}
        </View>;
    };

    const renderBottom = () => {
        if(variant === "info") {
            return null;
        }

        return <View
            style={[
                bottomContainerStyle,
                stylesheet.bottomContainer,
                bottomContainerDynamicStyle
            ]}
        >
            {bottomComponent || <View
                style={[
                    bottomContentContainerStyle,
                    stylesheet.bottomContentContainer,
                    bottomContentContainerDynamicStyle
                ]}
            >
                {secondaryButton()}
                {primaryButton()}
            </View>}
        </View>;
    };

    const secondaryButton = () => {
        if(variant !== "yes-no") {
            return null;
        }

        return <Button
            type="neutral"
            {...secondaryButtonProps}
            onPress={() => {
                if(secondaryButtonProps?.onPress) secondaryButtonProps.onPress({
                    closeAnimation: (onClosed?: (props: {
                        id: string;
                    }) => void) => {
                        closeAnimation(onClosed);
                    }
                });
            }}
            spreadBehaviour={contentJustify === "centered" ? "stretch" : "free"}
            title={secondaryButtonProps?.title || localize("cancel")}
            style={secondaryButtonStyle}
            variant="outline"
        />;
    };

    const primaryButton = () => {
        return <Button
            type="primary"
            size="medium"
            {...primaryButtonProps}
            spreadBehaviour={contentJustify === "centered" ? "stretch" : "free"}
            onPress={() => {
                if(primaryButtonProps?.onPress) primaryButtonProps?.onPress({
                    closeAnimation: (onClosed?: (props: {
                        id: string;
                    }) => void) => {
                        closeAnimation(onClosed);
                    }
                });
            }}
            title={primaryButtonProps?.title || localize("ok")}
            style={[
                primaryButtonStyle,
                stylesheet.primaryButton,
                primaryButtonDynamicStyle
            ]}
            variant="filled"
        />;
    };

    const renderCloseIcon = () => {
        if(variant !== "info") {
            return null;
        }

        if(contentJustify === "centered") {
            return null;
        }

        return <Button
            onPress={() => {
                if(onOverlayPress) onOverlayPress({
                    closeAnimation: (onClosed?: (props: {
                        id: string;
                    }) => void) => {
                        closeAnimation(onClosed);
                    }
                });
            }}
            icon={() => <XIcon
                color={closeIconProps.color}
                size={closeIconProps.size}
            />}
            style={[
                stylesheet.closeIcon,
                closeIconDynamicStyle
            ]}
            isCustomPadding={true}
            variant="ghost"
        />;
    };

    const renderDialog = () => {
        if(!isVisible) {
            return null;
        }

        return <Animated.View
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle,
                {
                    transform: [
                        {
                            scale: scaleAnim
                        }
                    ]
                }
            ]}
        >
            {renderCloseIcon()}
            {renderHeader()}
            <View
                style={[
                    contentContainerStyle,
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                {children || <Text
                    style={[
                        stylesheet.contentText,
                        contentTextDynamicStyle
                    ]}
                    variant="bodyLargeSize"
                >
                    {content}
                </Text>}
            </View>
            {renderBottom()}
        </Animated.View>;
    };

    if(!isVisible) {
        return null;
    }

    return withModal ? <Modal
        {...modalProps}
        onOverlayPress={() => {
            if(onOverlayPress) onOverlayPress({
                closeAnimation: (onClosed?: (props: {
                    id: string;
                }) => void) => {
                    closeAnimation(onClosed);
                }
            });
        }}
        id={`${id.current}-modal`}
        ref={modalRef}
    >
        {renderDialog()}
    </Modal> : renderDialog();
};
export default forwardRef(Dialog);
